/**
 * Unified Product Designs modal — Solar, Roof, and Battery design tabs in one shell.
 */
import React, { useState, useEffect, useCallback, type ReactNode } from 'react';
import { SunHorizon, House, BatteryCharging } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import type { RetrieveSystemDesignResponse } from '../../../../services/oneButtonServices/designServices';
import DashboardModal from './DashboardModal';
import { DesignArrayModal } from './DesignArrayModal';
import { RoofDetailsModal } from './RoofDetailsModal';
import BatteryDetailsModal from './BatteryDetailsModal';
import { useRef } from 'react';
import './oneButton-css/ProductDesignsModal.css';

export type ProductDesignsTab = 'solar' | 'roof' | 'battery' | 'rrr';

type RoofDetailsModalHandle = {
    updateRoofDetails: () => void;
};

type BatteryDetailsModalHandle = {
    updateBatteryDetails: () => void;
};

export interface ProductDesignsModalProps {
    open: boolean;
    onClose: () => void;
    initialTab: ProductDesignsTab;
    systemDesign: RetrieveSystemDesignResponse | null;
    designImageUrl: string | null;
    designImageName?: string;
    productionKwh: number | string | null;
    systemSizeKw: number | string | null;
    multiplier: number | null;
    systemDesignLosses: unknown;
    solarProductFinancePartner?: string | null;
    onOpenDesignHistoryV2?: () => void;
    onOpenDesignHistory?: () => void;
    batteryData: {
        manufacturer: string;
        name: string;
        quantity: string | number;
        rawName?: string;
    } | null;
}

const TABS: { key: ProductDesignsTab; label: string; Icon: typeof SunHorizon, enable:boolean }[] = [
    { key: 'solar', label: 'Solar', Icon: SunHorizon, enable:false },
    { key: 'roof', label: 'Roof', Icon: House, enable:false  },
    { key: 'battery', label: 'Battery', Icon: BatteryCharging, enable:false  },
    { key: 'rrr', label: 'RRR', Icon: House, enable:false  },
];

/** Preferred modal width per tab; CSS clamps to viewport. */
const TAB_MODAL_WIDTH: Record<ProductDesignsTab, number> = {
    battery: 720,
    solar: 1480,
    roof: 1280,
};

export const ProductDesignsModal: React.FC<ProductDesignsModalProps> = ({
    open,
    onClose,
    initialTab,
    systemDesign,
    designImageUrl,
    designImageName,
    productionKwh,
    systemSizeKw,
    multiplier,
    systemDesignLosses,
    solarProductFinancePartner,
    onOpenDesignHistoryV2,
    onOpenDesignHistory,
    batteryData,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const [activeTab, setActiveTab] = useState<ProductDesignsTab>(initialTab);
    const [tabFooter, setTabFooter] = useState<ReactNode>(null);

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);


    const RoofDetailsModalRef = useRef<RoofDetailsModalHandle | null>(null);
    const BatteryDetailsModalRef = useRef<BatteryDetailsModalHandle | null>(null);

    useEffect(() => {
        if (open){ 
            TABS.forEach((ele: any) => {
                const index = opportunityDetails.OpportunityProduct.findIndex((el:any) => el.Product_Type_Name.toLowerCase() === ele.key && el.ActiveFlag);
                if(index > -1){
                    ele.enable = true;
                } else {
                    ele.enable = false;
                }
            });
            setActiveTab(initialTab);
        }
    }, [open, initialTab]);

    useEffect(() => {
        if (!open) setTabFooter(null);
    }, [open]);

    const handleEmbeddedFooterChange = useCallback((footer: ReactNode | null) => {
        setTabFooter(footer);
    }, []);

    const handleTabClick = useCallback((tab: ProductDesignsTab, active: ProductDesignsTab) => {
        if(active == 'roof'){
            if(RoofDetailsModalRef.current){
                RoofDetailsModalRef.current?.updateRoofDetails();
            } 

        } else if(active == 'battery'){
            if(BatteryDetailsModalRef.current){
                BatteryDetailsModalRef.current?.updateBatteryDetails();
            } 
        } 
        if(tab == 'rrr') tab = 'roof';
        setActiveTab(tab);
    }, []);

    const tabBar = (
        <div className="obd-product-designs__tabs" role="tablist" aria-label="Product design type">
            {TABS.map(({ key, label, Icon, enable }) => (
                (enable &&
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === key}
                        className={`obd-product-designs__tab obd-product-designs__tab--${key} ${
                            activeTab === key ? 'obd-product-designs__tab--active' : ''
                        }`}
                        onClick={() => handleTabClick(key,activeTab)}
                    >
                        <Icon size={16} weight="regular" aria-hidden />
                        <span>{label}</span>
                    </button>
                )
            ))}
        </div>
    );

    const panelHidden = (tab: ProductDesignsTab) => (activeTab !== tab ? { display: 'none' as const } : undefined);

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title="Product designs"
            headerActions={tabBar}
            width={TAB_MODAL_WIDTH[activeTab]}
            centered
            maskClosable={false}
            theme={theme}
            destroyOnClose
            footer={tabFooter}
            className={`obd-product-designs-modal obd-product-designs-modal--${activeTab} obd-theme-${theme}`}
            rootClassName={`obd-product-designs-modal-root obd-product-designs-modal-root--${activeTab} obd-theme-${theme}`}
        >
            <div className="obd-product-designs__content">
                <div
                    role="tabpanel"
                    aria-hidden={activeTab !== 'solar'}
                    className="obd-product-designs__panel obd-product-designs__panel--solar"
                    style={panelHidden('solar')}
                >
                    <DesignArrayModal
                        embedded
                        embeddedActive={activeTab === 'solar'}
                        onEmbeddedFooterChange={handleEmbeddedFooterChange}
                        open={open}
                        onClose={onClose}
                        systemDesign={systemDesign}
                        designImageUrl={designImageUrl}
                        designImageName={designImageName}
                        productionKwh={productionKwh}
                        systemSizeKw={systemSizeKw}
                        multiplier={multiplier}
                        systemDesignLosses={systemDesignLosses}
                        solarProductFinancePartner={solarProductFinancePartner}
                        onOpenDesignHistoryV2={onOpenDesignHistoryV2}
                        onOpenDesignHistory={onOpenDesignHistory}
                    />
                </div>
                <div
                    role="tabpanel"
                    aria-hidden={activeTab !== 'roof'}
                    className="obd-product-designs__panel obd-product-designs__panel--roof"
                    style={panelHidden('roof')}
                >
                    <RoofDetailsModal
                        embedded
                        embeddedActive={activeTab === 'roof'}
                        onEmbeddedFooterChange={handleEmbeddedFooterChange}
                        open={open}
                        onClose={onClose}
                        ref={RoofDetailsModalRef}
                    />
                </div>
                <div
                    role="tabpanel"
                    aria-hidden={activeTab !== 'battery'}
                    className="obd-product-designs__panel obd-product-designs__panel--battery"
                    style={panelHidden('battery')}
                >
                    <BatteryDetailsModal
                        embedded
                        embeddedActive={activeTab === 'battery'}
                        onEmbeddedFooterChange={handleEmbeddedFooterChange}
                        open={open}
                        onClose={onClose}
                        batteryData={batteryData}
                        ref={BatteryDetailsModalRef}
                    />
                </div>
            </div>
        </DashboardModal>
    );
};

export default ProductDesignsModal;
